/**
 * SSE parsing utilities — framework-free, shared by every streaming consumer
 * (the companion chat loop here in edge-ai, plus the frontend's analyst/
 * research/transcript streams which re-export this via `@/lib/sse-parse`).
 *
 * Per the SSE spec a frame is terminated by a blank line, where each line
 * ending may be \r\n, \n, or \r — the boundary can be \r\n\r\n, \n\n, or \r\r.
 * sse-starlette emits \r\n\r\n by default; the FastAPI Vite proxy preserves it.
 */

export interface ParsedSseEvent {
  event: string
  data: unknown
  /**
   * The frame's `id:` line, if present. Optional and additive — most streams
   * don't send one, but sse-starlette emitters that journal events (deep
   * research) carry the event's monotonic `seq` here rather than in `data:`.
   */
  id?: string
}

export const FRAME_BOUNDARY = /\r\n\r\n|\n\n|\r\r/

/**
 * Parse a single SSE frame (text between blank-line separators).
 * Returns null if the frame produced no usable event.
 */
export function parseSseFrame(frame: string): ParsedSseEvent | null {
  let eventName = 'message'
  let id: string | undefined
  const dataLines: string[] = []

  for (const rawLine of frame.split('\n')) {
    const line = rawLine.replace(/\r$/, '')
    if (line === '' || line.startsWith(':')) continue
    if (line.startsWith('event:')) {
      eventName = line.slice(6).trim()
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trim())
    } else if (line.startsWith('id:')) {
      id = line.slice(3).trim()
    }
  }

  if (dataLines.length === 0) return null

  const payload = dataLines.join('\n')
  let data: unknown
  try {
    data = JSON.parse(payload)
  } catch {
    data = payload
  }
  return id === undefined ? { event: eventName, data } : { event: eventName, data, id }
}

/**
 * Pull every complete frame from the buffer, returning the parsed frame
 * strings (without trailing separator) plus the unconsumed remainder.
 *
 * Designed to be called repeatedly as new bytes arrive on a ReadableStream;
 * pass the accumulated buffer in, write `remaining` back to the buffer, and
 * feed each `frame` to `parseSseFrame`.
 */
export function drainFramesFromBuffer(buffer: string): { frames: string[]; remaining: string } {
  const frames: string[] = []
  let rest = buffer
  for (;;) {
    const match = FRAME_BOUNDARY.exec(rest)
    if (!match) break
    frames.push(rest.slice(0, match.index))
    rest = rest.slice(match.index + match[0].length)
  }
  return { frames, remaining: rest }
}
